import {
  Box,
  Divider,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableFoot,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import {
  AccountIcon,
  CheckIcon,
  DeleteIcon,
  PlusCircleOutlineIcon,
} from '@atgfr8/icons';
import { AutoComplete } from '@components';
import {
  CustomerLTLOverrideCarrierDetails,
  LTLCostPlusWithOverrides,
} from '@store/customers/customerDetails/LTL';
import { CarrierInfo } from '@typeDefinitions/carrierTypes';
import { Accessor, Index, Setter, Show } from 'solid-js';

import { InitialLTLFormValues } from '../Ltl';
import classes from './classes';

const MarginOverrides = (props: {
  setFields: (key: string, value: unknown) => void;
  data: Accessor<InitialLTLFormValues>;
  isValidCarrierChoice: (carrier: CarrierInfo) => boolean;
  setIsDirty: Setter<boolean>;
  onSectionChange: () => void;
}) => {
  const headers = [
    'Margin Overrides',
    'Amount',
    'Minimum',
    'Maximum',
    'Carriers',
    'Action',
  ];
  const dummyData: string[] = ['Data 1', 'Data 2', 'Data 3'];
  const cellStyles = {
    padding: '10px',
  };

  const addRow = () => {
    const lineItems = [...props.data().ltlCostPlusOverrides];
    lineItems.push({
      type: 'Flat',
      amount: null,
      minimum: null,
      maximum: null,
      ltlCostPlusOverrideCarriers: [],
    });
    props.setIsDirty(true);
    props.onSectionChange();
    props.setFields('ltlCostPlusOverrides', lineItems);
  };

  const updateLineItems = (
    key: string,
    value: string | number | null,
    index: number,
  ) => {
    const lineItems = [...props.data().ltlCostPlusOverrides];
    lineItems[index] = {
      ...lineItems[index],
      [key]: value,
    };
    props.setIsDirty(true);
    props.onSectionChange();
    props.setFields('ltlCostPlusOverrides', lineItems);
  };

  const onDelete = (item: CustomerLTLOverrideCarrierDetails, index: number) => {
    const lineItems = [...props.data().ltlCostPlusOverrides];
    lineItems[index].ltlCostPlusOverrideCarriers = lineItems[
      index
    ].ltlCostPlusOverrideCarriers.filter(
      (i) => i.carrierName !== item.carrierName,
    );
    props.setIsDirty(true);
    props.onSectionChange();
    props.setFields('ltlCostPlusOverrides', lineItems);
  };

  const onItemSelect = (item: CarrierInfo, index: number) => {
    if (!props.isValidCarrierChoice(item)) {
      const lineItems = [...props.data().ltlCostPlusOverrides];
      lineItems[index].ltlCostPlusOverrideCarriers = [
        ...lineItems[index].ltlCostPlusOverrideCarriers,
        {
          carrierName: item.name,
          carrierId: item.id,
          mC: item.mC,
          dOT: item.dOT,
          status: item.status,
          ltlEnabled: item.lTLEnabled,
        } as CustomerLTLOverrideCarrierDetails,
      ];
      props.setIsDirty(true);
      props.onSectionChange();
      props.setFields('ltlCostPlusOverrides', lineItems);
    }
  };

  return (
    <Box class="">
      <Box class={classes.marginOverride}>Margin Overrides</Box>
      <Divider class="mb-3! h-[2px]!" orientation="horizontal" />
      <div class="text-black! [&_td]:!border-[#e0e0e0] [&_th]:!border-[#e0e0e0] [&_th]:!text-black">
        <Table>
          <TableHead class="head">
            <TableRow>
              {headers.map((item) => (
                <TableHeaderCell>{item}</TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <Show when={dummyData.length > 0}>
            <TableBody class={classes.tableBg}>
              <Index each={props.data().ltlCostPlusOverrides}>
                {(item: Accessor<LTLCostPlusWithOverrides>, index: number) => (
                  <>
                    <TableRow class={classes.tableRow}>
                      <TableCell
                        style={{
                          ...cellStyles,
                          'max-width': '50px',
                        }}
                      >
                        <Box class={classes.checkboxContainer}>
                          <Box
                            class={`text-[16px] ${
                              item().type === 'Percentage'
                                ? classes.greyText
                                : classes.blackText
                            }`}
                          >
                            Flat
                          </Box>
                          <Switch
                            aria-label="Margin override type"
                            dataTestId={`margin-overrides-type-switch-${index}`}
                            checked={item().type === 'Percentage'}
                            onChange={() => {
                              updateLineItems(
                                'type',
                                item().type !== 'Percentage'
                                  ? 'Percentage'
                                  : 'Flat',
                                index,
                              );
                              updateLineItems('minimum', null, index);
                              updateLineItems('maximum', null, index);
                            }}
                          />
                          <Box
                            class={`text-[16px] ${
                              item().type === 'Percentage'
                                ? classes.blackText
                                : classes.greyText
                            }`}
                          >
                            Percentage
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell
                        style={{
                          ...cellStyles,
                          'min-width': '130px',
                          'max-width': '130px',
                          'white-space': 'nowrap',
                        }}
                      >
                        <Box class="flex items-start">
                          <TextField
                            placeholder="Amount"
                            id="full-text-Amount"
                            size="sm"
                            type="number"
                            value={
                              props.data().ltlCostPlusOverrides[index]
                                .amount as number
                            }
                            onChange={(val: unknown) =>
                              updateLineItems('amount', val as number, index)
                            }
                            startIcon={
                              props.data().ltlCostPlusOverrides[index].type ===
                              'Percentage'
                                ? undefined
                                : '$'
                            }
                            style={{
                              width: '129.01px',
                            }}
                            maxLength={16}
                            dataTestId={`margin-overrides-amount-textInput-${index}`}
                          />
                          <Box
                            style={{
                              'margin-left': '5px',
                              'margin-top': '8px',
                            }}
                          >
                            {props.data().ltlCostPlusOverrides[index].type ===
                            'Percentage'
                              ? '%'
                              : 'USD'}
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell
                        style={{
                          ...cellStyles,
                          'min-width': '100px',
                          'max-width': '100px',
                        }}
                      >
                        <Show
                          when={
                            props.data().ltlCostPlusOverrides[index].type ===
                            'Percentage'
                          }
                        >
                          <TextField
                            placeholder="Minimum"
                            id="full-text-Minimum"
                            size="sm"
                            type="number"
                            showInCurrencyFormat
                            value={
                              props.data().ltlCostPlusOverrides[index]
                                .minimum as number
                            }
                            onChange={(val: unknown) =>
                              updateLineItems('minimum', val as number, index)
                            }
                            maxLength={16}
                            dataTestId={`margin-overrides-minimum-textInput-${index}`}
                          />
                        </Show>
                      </TableCell>
                      <TableCell
                        style={{
                          ...cellStyles,
                          'min-width': '100px',
                          'max-width': '100px',
                        }}
                      >
                        <Show
                          when={
                            props.data().ltlCostPlusOverrides[index].type ===
                            'Percentage'
                          }
                        >
                          <TextField
                            placeholder="Maximum"
                            id="full-text-Maximum"
                            size="sm"
                            type="number"
                            showInCurrencyFormat
                            value={
                              props.data().ltlCostPlusOverrides[index]
                                .maximum as number
                            }
                            onChange={(val: unknown) =>
                              updateLineItems('maximum', val as number, index)
                            }
                            maxLength={16}
                            dataTestId={`margin-overrides-maximum-textInput-${index}`}
                          />
                        </Show>
                      </TableCell>
                      <TableCell
                        style={{
                          ...cellStyles,
                          'min-width': '170px',
                          'max-width': '170px',
                        }}
                      >
                        <AutoComplete<CarrierInfo>
                          id="autocomplete-lane-search-delivering-to"
                          endpoint="ltl/autocomplete/?term="
                          onItemSelect={(item) =>
                            onItemSelect(
                              {
                                mC: item.mC,
                                dOT: item.dOT,
                                id: item.id,
                                name: item.name,
                                status: item.status,
                                lTLEnabled: item.lTLEnabled,
                              },
                              index,
                            )
                          }
                          multipleSelectValues={props
                            .data()
                            .ltlCostPlusOverrides[
                              index
                            ].ltlCostPlusOverrideCarriers.map((item) => {
                              return {
                                mC: item.mC,
                                dOT: item.dOT,
                                id: item.carrierId,
                                name: item.carrierName,
                                status: item.status,
                                lTLEnabled: item.ltlEnabled,
                              } as CarrierInfo;
                            })}
                          onMultipleDelete={(item) => {
                            onDelete(
                              {
                                carrierName: item.name,
                                carrierId: item.id,
                                mC: item.mC,
                                dOT: item.dOT,
                                status: item.status,
                                ltlEnabled: item.lTLEnabled,
                              } as CustomerLTLOverrideCarrierDetails,
                              index,
                            );
                          }}
                          label=""
                          renderMultipleKey="name"
                          mode="multiple"
                          defaultValue=""
                          placeholder="Add one or more carriers"
                          version="v3"
                          size="small"
                          renderItem={(item) => {
                            return (
                              <Box class="flex gap-1">
                                <AccountIcon class="text-[#026EA1]" />
                                <Box class="font-medium text-base">
                                  {item.name}

                                  <Box class={classes.carrierMcDotText}>
                                    MC:{' '}
                                    <span class="text-sm! font-normal">
                                      {item.mC}
                                    </span>{' '}
                                    | DOT:{' '}
                                    <span class="text-sm! font-normal">
                                      {item.dOT}
                                    </span>
                                  </Box>
                                  <Box class={classes.carrierMcDotText}>
                                    Status:{' '}
                                    <span
                                      class={`font-bold ${
                                        item.status === 'Active'
                                          ? 'text-[#28957C]'
                                          : 'text-[#B00020]'
                                      }`}
                                    >
                                      {item.status}
                                    </span>
                                  </Box>
                                </Box>

                                {item.lTLEnabled && (
                                  <Box class="flex flex-col w-14">
                                    <span class={classes.carrierLTLBox}>
                                      LTL
                                      <CheckIcon class="text-[13px]! text-white" />
                                    </span>
                                  </Box>
                                )}
                              </Box>
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            const lineItems = [
                              ...props.data().ltlCostPlusOverrides,
                            ];
                            lineItems.splice(index, 1);
                            props.onSectionChange();
                            props.setFields('ltlCostPlusOverrides', lineItems);
                          }}
                          class="relative! top-[8px]!"
                          dataTestId="margin-overrides-delete-icon-button"
                        >
                          <DeleteIcon class={classes.deleteIcon} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </Index>
            </TableBody>
          </Show>
          <TableFoot class={classes.tableFooter}>
            <TableRow class={classes.tableFooterRow}>
              <TableCell colSpan={1}>
                <Box class="flex items-center">
                  <PlusCircleOutlineIcon
                    class={classes.addIcon}
                    onClick={addRow}
                  />
                  <Box class={classes.addButton}>Add Override</Box>
                </Box>
              </TableCell>
              <TableCell colSpan={5}></TableCell>
            </TableRow>
          </TableFoot>
        </Table>
      </div>
    </Box>
  );
};

export default MarginOverrides;
